// React
import { useCallback, useState } from "react";

// MUI
import { Typography } from "@mui/material";

// Components
import GeneralDialog from "@Layouts/components/Dialogs/GeneralDialog";
import KeyManagementServiceTable from "@DataPages/components/Inventory/Tables/KeyActivityManagementTable";
import ImageCarousel from "@DataPages/components/Inventory/Widgets/ImageCarousel";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";

// Types
import { KMS } from "@DataPages/types/inventory/models/kms";
import { IPageParam } from "@/Common/api/types/pagination";

// React Query
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { getKeyEventImagesByKiosk, getKeyManagement } from "@/DataPages/api/inventory/queries";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";

// Constants
import { InventoryTab } from "./constants";
import { EMPTY_RESULT, INITIAL_PAGE_PARAM, PAGINATION_SIZE } from "@/DataPages/constants/pagination";

// Hooks
import { useFilteredData } from "./hooks/useFilteredData";
import { useInventoryContext } from "./contexts/InventoryFilterContext";
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";

export default function KeyActivityManagement() {
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState<Partial<KMS> | null>(null);
	const { commissionCycleId, payPeriodId } = useInventoryContext();

	/********************************************** QUERIES **********************************************/
	const {
		data: rows,
		isLoading,
		hasNextPage,
	} = usePaginatedResource({
		queryKey: ["inventory", "keyManagement", locationId, payPeriodId],
		queryFn: async (params) => {
			const pageParam = params.pageParam as IPageParam<string> | undefined;
			const pagination = pageParam && typeof pageParam === "object" ? pageParam : INITIAL_PAGE_PARAM;
			if (!("after" in pagination) || !("first" in pagination)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}
			const paginatedResponse = await getKeyManagement(locationId as string, payPeriodId, pagination);
			if (!paginatedResponse) {
				return EMPTY_RESULT;
			}
			return paginatedResponse;
		},
		initialPageParam: INITIAL_PAGE_PARAM,
		getNextPageParam: buildGetNextPageParam(PAGINATION_SIZE),
		enabled: Boolean(locationId) && Boolean(payPeriodId),
		fetchAllPages: true,
		staleTime: Infinity,
		gcTime: Infinity,
	});

	const { data: keyEventImages, isLoading: isLoadingKeyEventImagesService } = useQuery({
		queryKey: ["getKeyEventImagesByKiosk", locationId, selectedEvent?.kioskId, selectedEvent?.creationDate],
		queryFn: async () => {
			const resp = await getKeyEventImagesByKiosk(
				locationId as string,
				selectedEvent?.kioskId as string,
				selectedEvent?.creationDate as string
			);
			return resp?.map((image: string) => ({ src: image, alt: "Image" }));
		},
		enabled: Boolean(locationId) && Boolean(selectedEvent?.kioskId) && Boolean(selectedEvent?.creationDate),
	});

	/**************************************** HANDLERS ****************************************/
	const handleImageDialogOpen = useCallback((row: KMS) => {
		const event = row;
		setSelectedEvent(event);
		setIsImageDialogOpen(true);
	}, []);

	const handleImageDialogClose = () => {
		setCurrentIndex(0);
		setIsImageDialogOpen(false);
		setSelectedEvent(null); // Clear the selected event so re-selecting triggers fresh or cached fetch
	};

	const handleChangeImageIndex = (index: number) => {
		setCurrentIndex(index);
	};

	const kmsRows = useFilteredData({
		data: rows ?? [],
		currentOrigin: InventoryTab.KMS,
		pathToVinNumber: "vinNumber",
		commissionCycleId,
		payPeriodId,
	});

	if (isLoading) {
		return <TableLoader toolbar buttons={3} content rows={5} multiTitles={8} className={"!mt-8"} toolbarClasses={"!mb-8"} />;
	}

	return (
		<>
			<GeneralDialog
				open={isImageDialogOpen}
				onClose={handleImageDialogClose}
				title={
					<Typography
						component={"span"}
						className="!text-[#666666] !text-[12px] !font-montserrat !ml-[-5px] !absolute !top-[22px] !left-[50%] !-translate-x-1/2"
					>
						{keyEventImages?.length ? `${currentIndex + 1}/${keyEventImages.length}` : ""}
					</Typography>
				}
				iconTitle={null}
				showDivider={false}
				closeButtonClasses={"!absolute !top-2 !right-[22px]"}
			>
				{isLoadingKeyEventImagesService && <LoadingSpinner label="Loading images..." className="!mt-8 !mb-8" />}
				{keyEventImages?.length && (
					<ImageCarousel images={keyEventImages ?? []} onChangeImageIndex={handleChangeImageIndex} currentIndex={currentIndex} />
				)}
			</GeneralDialog>
			<KeyManagementServiceTable
				key={4}
				rows={kmsRows}
				isLoading={isLoading}
				onClickImage={handleImageDialogOpen}
				hasNextPage={hasNextPage}
			/>
		</>
	);
}
